/** Client-side biological day context passed to protocol resolvers. */

import { normalizeLanguageCode, type AppLanguage } from '../i18n/languages'

export function clientLocalDate(): string {
  return new Date().toLocaleDateString('en-CA')
}

export function clientTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** Best-effort ISO country guess from the browser locale (e.g. "de-AT" -> "AT"). */
export function clientCountryGuess(fallback = 'AT'): string {
  for (const locale of navigator.languages ?? [navigator.language]) {
    try {
      const region = new Intl.Locale(locale).region
      if (region && /^[A-Z]{2}$/.test(region)) return region
    } catch {
      // ignore malformed locale strings
    }
  }
  return fallback
}

/** Best-effort app language from browser locale (e.g. "de-AT" -> "de"). */
export function clientLanguageGuess(): AppLanguage {
  for (const locale of navigator.languages ?? [navigator.language]) {
    return normalizeLanguageCode(locale)
  }
  return normalizeLanguageCode(navigator.language)
}

export function clientProtocolContext() {
  return {
    client_local_date: clientLocalDate(),
    client_timezone: clientTimezone(),
    client_language: clientLanguageGuess(),
  }
}

export function formatDateForUser(isoOrDate: string | Date, language: AppLanguage, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  return date.toLocaleDateString(language, options)
}

export function formatTimeForUser(isoOrDate: string | Date, language: AppLanguage, options?: Intl.DateTimeFormatOptions): string {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  return date.toLocaleTimeString(language, options)
}
